import { useMemo, useRef, useState } from 'react';
import { Image as ImageIcon, Film, Loader2, Music, Plus, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { uploadFileViaPresignedUrl } from '@/lib/exportMediaStaging';
import BookendTitleOverlay from '@/components/BookendTitleOverlay';
import SubtitleLookControls from '@/components/subtitles/SubtitleLookControls';
import { computeSubtitleLayerTextLayout } from '@/lib/subtitles/subtitleEditorLayout';
import {
  bookendDefaultTitleStyle,
  bookendSlotHasMedia,
  bookendSlotMediaUrl,
  normalizeVideoBookends,
  patchBookendTitleLayer,
} from '@/lib/videoEditorBookends';
import { normalizeVideoSubtitleStyle } from '@/lib/videoEditorProjectFile';

function displayUrlForInput(url) {
  const s = String(url || '').trim();
  if (!s || s.startsWith('blob:')) return '';
  return s;
}

function BookendSlotEditor({
  slotKey,
  label,
  slot,
  defaultTitle,
  aspectRatio,
  narrationProjectId,
  storyId,
  onChange,
  onPersist,
}) {
  const fileRef = useRef(null);
  const previewRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [previewBlobUrl, setPreviewBlobUrl] = useState('');
  const [snapToGrid, setSnapToGrid] = useState(false);
  const [titleEditing, setTitleEditing] = useState(() => Boolean(slot.title?.trim()));

  const titleStyle = slot.titleStyle || bookendDefaultTitleStyle(aspectRatio);

  const patch = (partial, { persist = false } = {}) => {
    onChange({
      ...slot,
      ...partial,
    });
    if (persist) onPersist?.();
  };

  const bookendMediaPatch = (url) => {
    const trimmed = String(url || '').trim();
    const urlS3 = trimmed.includes('amazonaws.com') ? trimmed : '';
    return {
      url: trimmed,
      ...(urlS3 ? { url_s3_url: urlS3 } : {}),
    };
  };

  const patchTitleStyle = (nextStyle) => {
    patch({
      titleStyle: normalizeVideoSubtitleStyle(nextStyle, aspectRatio),
    });
  };

  const onFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    const isVideo = file.type.startsWith('video/');
    const isAudio = file.type.startsWith('audio/');
    const localPreview = URL.createObjectURL(file);
    setPreviewBlobUrl(localPreview);
    setUploading(true);

    try {
      const persistedUrl = await uploadFileViaPresignedUrl(file, {
        narrationProjectId,
        storyId,
        onProgress: (msg) => toast.info(msg),
      });
      const nextTitle = slot.title || defaultTitle || '';
      patch({
        enabled: true,
        ...bookendMediaPatch(persistedUrl),
        mediaType: isVideo ? 'video' : isAudio ? 'audio' : 'image',
        title: nextTitle,
        titleStyle: slot.titleStyle || bookendDefaultTitleStyle(aspectRatio),
      }, { persist: true });
      if (nextTitle) setTitleEditing(true);
      toast.success(`${isVideo ? 'Video' : isAudio ? 'Audio' : 'Image'} uploaded for ${label.toLowerCase()}`);
    } catch (err) {
      console.error(err);
      toast.error(err?.message || `Could not upload ${label.toLowerCase()} media`);
    } finally {
      URL.revokeObjectURL(localPreview);
      setPreviewBlobUrl('');
      setUploading(false);
    }
  };

  const previewSrc = previewBlobUrl
    || (bookendSlotHasMedia(slot) ? bookendSlotMediaUrl(slot) : '');

  const hasMedia = bookendSlotHasMedia(slot);
  const showPanel = Boolean(slot.enabled) || hasMedia;

  const removeMedia = () => {
    patch({
      url: '',
      url_s3_url: '',
      enabled: false,
    }, { persist: true });
    setTitleEditing(false);
  };

  const titlePreviewLayout = useMemo(() => {
    if (!previewRef.current) return { fontPx: 0 };
    const w = previewRef.current.clientWidth || 320;
    const h = previewRef.current.clientHeight || 180;
    const layer = titleStyle.layer;
    if (!layer) return { fontPx: 0 };
    return computeSubtitleLayerTextLayout({
      text: slot.title || defaultTitle || 'Title',
      layer,
      style: titleStyle,
      frameW: w,
      frameH: h,
      aspectRatio,
      viewportMin: Math.min(w, h),
    });
  }, [slot.title, defaultTitle, titleStyle, aspectRatio]);

  const showTitleOverlay = Boolean(previewSrc && !uploading && titleEditing);

  const removeTitle = () => {
    patch({ title: '' });
    setTitleEditing(false);
  };

  const addTitle = () => {
    const next = slot.title.trim() || defaultTitle || 'Title';
    patch({
      title: next,
      titleStyle: slot.titleStyle || bookendDefaultTitleStyle(aspectRatio),
    });
    setTitleEditing(true);
  };

  return (
    <div
      className="rounded-lg border p-3 space-y-3"
      style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-secondary)' }}
      data-testid={`video-bookend-${slotKey}`}
    >
      <div className="flex items-center justify-between gap-2">
        <Label className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
          {label}
        </Label>
        <Switch
          checked={Boolean(slot.enabled)}
          onCheckedChange={(checked) => {
            const nextTitle = slot.title || defaultTitle || '';
            patch({
              enabled: checked,
              title: nextTitle,
              titleStyle: slot.titleStyle || bookendDefaultTitleStyle(aspectRatio),
            }, { persist: true });
            if (checked && nextTitle.trim()) setTitleEditing(true);
          }}
          data-testid={`video-bookend-${slotKey}-enabled`}
        />
      </div>

      {!slot.enabled && hasMedia ? (
        <p className="text-[10px] rounded px-2 py-1" style={{ color: 'var(--text-tertiary)', background: 'var(--bg-primary)' }}>
          Card is off for export. Uploaded media is kept until you remove it.
        </p>
      ) : null}

      {showPanel ? (
        <>
          {slot.enabled ? (
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>
                  Media type
                </Label>
                <Select
                  value={slot.mediaType}
                  onValueChange={(v) => patch({ mediaType: v === 'video' ? 'video' : 'image' }, { persist: true })}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="image">Image</SelectItem>
                    <SelectItem value="video">Video</SelectItem>
                    <SelectItem value="audio">Audio</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>
                  Duration ({slot.durationSeconds}s)
                </Label>
                <Slider
                  value={[slot.durationSeconds]}
                  min={1}
                  max={15}
                  step={1}
                  onValueChange={([v]) => patch({ durationSeconds: v }, { persist: true })}
                />
              </div>
            </div>
          ) : null}

          <div className="space-y-1">
            <div className="flex items-center justify-between gap-2">
              <Label className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>
                {slot.mediaType === 'video' ? 'Video' : slot.mediaType === 'audio' ? 'Audio' : 'Image'} URL or upload
              </Label>
              {hasMedia ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 px-2 text-[10px] gap-1"
                  style={{ borderColor: 'var(--glass-border)', color: 'var(--text-secondary)' }}
                  onClick={removeMedia}
                  data-testid={`video-bookend-${slotKey}-remove-media`}
                >
                  <Trash2 className="w-3 h-3" />
                  Remove media
                </Button>
              ) : null}
            </div>
            <div className="flex gap-2">
              <Input
                className="h-9 text-xs flex-1"
                value={displayUrlForInput(slot.url)}
                placeholder={uploading ? 'Uploading to cloud…' : 'https://… or upload'}
                onChange={(e) => patch(bookendMediaPatch(e.target.value))}
                onBlur={() => onPersist?.()}
                disabled={uploading}
                data-testid={`video-bookend-${slotKey}-url`}
              />
              <button
                type="button"
                className="h-9 px-3 rounded-lg border text-xs shrink-0 flex items-center gap-1 disabled:opacity-50"
                style={{ borderColor: 'var(--glass-border)', color: 'var(--text-secondary)' }}
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                data-testid={`video-bookend-${slotKey}-upload`}
              >
                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                {uploading ? 'Uploading' : 'Upload'}
              </button>
              <input
                ref={fileRef}
                type="file"
                accept={slot.mediaType === 'video' ? 'video/*' : slot.mediaType === 'audio' ? 'audio/*' : 'image/*'}
                className="hidden"
                onChange={onFile}
              />
            </div>
          </div>

          {previewSrc ? (
            <div className="space-y-2">
              {slot.enabled ? (
                <>
                  <div className="flex items-center justify-between gap-2">
                    <Label className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>
                      Title on card
                    </Label>
                    <div className="flex items-center gap-1">
                      {!titleEditing || !slot.title.trim() ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-7 px-2 text-[10px] gap-1"
                          style={{ borderColor: 'var(--glass-border)', color: 'var(--text-secondary)' }}
                          onClick={addTitle}
                          data-testid={`video-bookend-${slotKey}-add-title`}
                        >
                          <Plus className="w-3 h-3" />
                          Add title
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-7 px-2 text-[10px] gap-1"
                          style={{ borderColor: 'var(--glass-border)', color: 'var(--text-secondary)' }}
                          onClick={removeTitle}
                          data-testid={`video-bookend-${slotKey}-delete-title`}
                        >
                          <Trash2 className="w-3 h-3" />
                          Delete
                        </Button>
                      )}
                    </div>
                  </div>
                  <p className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>
                    Drag to move · handles to resize · double-click to edit text (same as subtitles).
                  </p>
                </>
              ) : null}
              <div
                ref={previewRef}
                className="relative aspect-video rounded-md overflow-hidden border"
                style={{ borderColor: 'var(--glass-border)' }}
              >
                {slot.mediaType === 'video' ? (
                  <video src={previewSrc} className="w-full h-full object-cover" muted playsInline controls />
                ) : slot.mediaType === 'audio' ? (
                  <div className="w-full h-full flex flex-col items-center justify-center gap-2 p-4 bg-slate-900/80">
                    <Music className="w-8 h-8 text-violet-300" />
                    <audio src={previewSrc} className="w-full" controls />
                  </div>
                ) : (
                  <img src={previewSrc} alt="" className="w-full h-full object-cover" />
                )}
                {uploading ? (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/40 z-30">
                    <Loader2 className="w-8 h-8 text-white animate-spin" />
                  </div>
                ) : null}
                {showTitleOverlay && slot.enabled ? (
                  <BookendTitleOverlay
                    containerRef={previewRef}
                    aspectRatio={aspectRatio}
                    titleStyle={titleStyle}
                    text={slot.title}
                    viewportMin={Math.min(previewRef.current?.clientWidth || 320, previewRef.current?.clientHeight || 180)}
                    snapToGrid={snapToGrid}
                    onLayerChange={(layer) => onChange(patchBookendTitleLayer(slot, layer, aspectRatio))}
                    onTextChange={(nextText) => {
                      const trimmed = String(nextText || '').trim();
                      if (!trimmed) {
                        removeTitle();
                        return;
                      }
                      patch({ title: trimmed });
                    }}
                  />
                ) : null}
              </div>
              {slot.enabled && titleEditing && slot.title.trim() ? (
                <div
                  className="rounded-lg border p-2.5"
                  style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-primary)' }}
                >
                  <p className="text-[10px] font-semibold uppercase tracking-wide mb-2" style={{ color: 'var(--text-tertiary)' }}>
                    Title style
                  </p>
                  <SubtitleLookControls
                    style={titleStyle}
                    onChange={patchTitleStyle}
                    previewFontPx={titlePreviewLayout.fontPx}
                    showSnapToGrid
                    snapToGrid={snapToGrid}
                    onSnapToGridChange={setSnapToGrid}
                    testIdPrefix={`video-bookend-${slotKey}-title`}
                  />
                </div>
              ) : null}
            </div>
          ) : (
            <div
              className="flex items-center justify-center gap-2 aspect-video rounded-md border border-dashed text-xs"
              style={{ borderColor: 'var(--glass-border)', color: 'var(--text-tertiary)' }}
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Uploading…
                </>
              ) : (
                <>
                  {slot.mediaType === 'video' ? <Film className="w-4 h-4" /> : slot.mediaType === 'audio' ? <Music className="w-4 h-4" /> : <ImageIcon className="w-4 h-4" />}
                  Add {slot.mediaType === 'video' ? 'video' : slot.mediaType === 'audio' ? 'audio' : 'image'} for {label.toLowerCase()}
                </>
              )}
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}

export function VideoEditorBookendsPanel({
  bookends,
  defaultTitle = '',
  aspectRatio = '16:9',
  narrationProjectId = null,
  storyId = null,
  onChange,
  onPersist,
}) {
  const setSlot = (key, slot) => {
    onChange((prev) => ({
      ...normalizeVideoBookends(prev, { defaultTitle, aspectRatio }),
      [key]: slot,
    }));
  };

  return (
    <div className="space-y-3 py-2 border-t" style={{ borderColor: 'var(--glass-border)' }}>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>
          Intro & outro cards
        </p>
        <p className="text-[11px] mt-1" style={{ color: 'var(--text-tertiary)' }}>
          Optional thumbnail image or video at the start and end of the export. Title text is draggable,
          resizable, and styled like subtitles.
        </p>
      </div>
      <BookendSlotEditor
        slotKey="intro"
        label="Opening card"
        slot={bookends.intro}
        defaultTitle={defaultTitle}
        aspectRatio={aspectRatio}
        narrationProjectId={narrationProjectId}
        storyId={storyId}
        onChange={(slot) => setSlot('intro', slot)}
        onPersist={onPersist}
      />
      <BookendSlotEditor
        slotKey="outro"
        label="Closing card"
        slot={bookends.outro}
        defaultTitle={defaultTitle}
        aspectRatio={aspectRatio}
        narrationProjectId={narrationProjectId}
        storyId={storyId}
        onChange={(slot) => setSlot('outro', slot)}
        onPersist={onPersist}
      />
    </div>
  );
}
